import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Lock, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const errorText = (detail, fallback) =>
  typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map((d) => d?.msg).filter(Boolean).join(" ") || fallback : fallback;

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const token = new URLSearchParams(window.location.search).get("token") || "";
  const [valid, setValid] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) { setValid(false); return; }
    axios.get("/auth/reset-password/verify", { params: { token } }).then(({ data }) => setValid(data.valid)).catch(() => setValid(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return setError(t("auth.reset.tooShort"));
    if (password !== confirm) return setError(t("auth.reset.mismatch"));
    setLoading(true); setError("");
    try {
      await axios.post("/auth/reset-password", { token, new_password: password });
      toast.success(t("auth.reset.success"));
      navigate("/login", { replace: true });
    } catch (err) { setError(errorText(err.response?.data?.detail, t("auth.reset.invalidLink"))); }
    finally { setLoading(false); }
  };

  const field = (id, value, setter, label, testId) => (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{label}</Label>
      <div className="relative">
        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input id={id} data-testid={testId} type="password" required autoComplete="new-password" value={value}
          onChange={(e) => setter(e.target.value)} className="pl-10 h-12 bg-white border-slate-200" />
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-10">
          <div className="w-8 h-8 bg-indigo-600 rounded-sm flex items-center justify-center"><Building2 className="w-4 h-4 text-white" /></div>
          <span className="font-display font-semibold text-slate-900 uppercase tracking-tight">SitePulse</span>
        </div>
        <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight uppercase mb-2">{t("auth.reset.title")}</h1>
        {valid === null && <p className="text-sm text-slate-500" data-testid="reset-password-checking">{t("common.loading")}</p>}
        {valid === false && (
          <div className="space-y-4" data-testid="reset-password-invalid">
            <p className="text-sm text-red-600">{t("auth.reset.invalidLink")}</p>
            <Link to="/login" className="text-sm text-indigo-600 hover:underline" data-testid="reset-password-login-link">{t("auth.reset.backToLogin")}</Link>
          </div>
        )}
        {valid && (
          <form onSubmit={submit} noValidate className="space-y-5 mt-6" data-testid="reset-password-form">
            {field("new-password", password, setPassword, t("auth.reset.newPassword"), "reset-password-new")}
            {field("confirm-password", confirm, setConfirm, t("auth.reset.confirmPassword"), "reset-password-confirm")}
            <p className="text-xs text-slate-400">{t("auth.reset.signOutNote")}</p>
            {error && <p role="alert" data-testid="reset-password-error" className="text-sm text-red-600">{error}</p>}
            <Button type="submit" data-testid="reset-password-submit" disabled={loading}
              className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-xs uppercase tracking-widest rounded-sm">
              {loading ? t("auth.reset.saving") : t("auth.reset.save")}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
